import React, { useEffect, useReducer, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import PropertyDetailListComponent from './PropertyDetailListComponent';
import { propertyDetailsValidator } from '../../validators';
import { useToast } from 'hooks/toasts/useToast';
import { useValidation, isValid } from '@totalsoft/pure-validations-react';
import { initialState, reducer } from '../reducers/propertyDetails/containerReducer';
import { ADD_UPDATE_PROPERTY_DETAILS } from '../../mutations';
import { PROPERTY_DETAILS_UPDATED } from '../../subscriptions';
import { PAGED_DETAILS_QUERY } from '../../queries';
import { useApolloClient } from '@apollo/client';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';

const defaultPager = {
  afterId: 0,
  totalCount: 0,
  pageSize: 10,
  direction: 1,
  page: 0
};

const PropertyDetailListContainer = ({ propertyId, propertyTypeId }) => {
  const [pager, setPager] = useState(defaultPager);
  const [filters, setFilters] = useState({});
  const confirm = useConfirmDialog();

  const {
    data,
    loading: loadingPagedDetails,
    refetch
  } = useQuery(PAGED_DETAILS_QUERY, {
    variables: {
      pager: {
        pageSize: pager.pageSize,
        afterId: pager.afterId,
        sortBy: pager.sortBy,
        direction: pager.direction,
        page: pager.page
      },
      propertyId: propertyId,
      filters
    },
    onCompleted: data => {
      if (data && pager.totalCount !== data.pagedDetails?.pagination?.totalCount) {
        setPager(currentPager => ({
          ...currentPager,
          totalCount: data.pagedDetails.pagination.totalCount
        }));
      }
    }
  });

  const addToast = useToast();
  const { t } = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [validation, validate] = useValidation(propertyDetailsValidator);
  const client = useApolloClient();

  const isNewProperty = useMemo(() => !propertyId, [propertyId]);

  const [addOrUpdatePropertyDetails] = useMutation(ADD_UPDATE_PROPERTY_DETAILS);

  useSubscription(PROPERTY_DETAILS_UPDATED, {
    onData: _ => {
      addToast(t('Property.PropertyDetailsUpdated'), 'success');
      dispatch({ type: 'OnPropertyDetailUpdated' });

      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedDetails' });
      client.cache.gc();
    }
  });

  const handleAdd = () => {
    dispatch({
      type: 'OnPropertyDetailAdded',
      propertyId
    });
  };

  const handlePropertyChanged = (propName, propValue, index) => {
    dispatch({ type: 'OnPropertyDetailChanged', propName, propValue, index });
  };

  const handleDelete = useCallback(
    index => {
      confirm(
        () => {
          dispatch({ type: 'OnPropertyDetailDeleted', index });
        },
        {
          title: t('Property.DeletePropertyDetail'),
          content: t('Property.AreYouSureDeleteProperty')
        }
      );
    },
    [confirm, t]
  );

  const handleCancel = index => {
    dispatch({ type: 'OnCancelPropertyDetail', index });
  };

  const handleEdit = index => {
    dispatch({ type: 'OnEditPropertyDetail', index });
  };

  const handleSave = useCallback(() => {
    const input = {
      // eslint-disable-next-line no-unused-vars
      propertyDetails: localState.values.map(({ inEdit, editValue, ...x }) => ({
        ...x,
        propertyValue: x.propertyValue.toString()
      })),
      propertyId: propertyId,
      pager: {
        pageSize: pager.pageSize,
        afterId: pager.afterId,
        sortBy: pager.sortBy,
        direction: pager.direction,
        page: pager.page
      },
      filters
    };
    setPager({ ...pager, page: 0 });
    setSaveDisabled(true);
    addOrUpdatePropertyDetails({ variables: { input } });
  }, [addOrUpdatePropertyDetails, filters, localState.values, pager, propertyId]);

  useEffect(() => {
    if (data?.pagedDetails?.values) {
      const values = data.pagedDetails.values;
      dispatch({ type: 'OnInitState', values });
    }
  }, [data]);

  useEffect(() => {
    validate(localState.values);
  }, [localState, validate]);

  useEffect(() => {
    setSaveDisabled(!(validation |> isValid));
  }, [localState, validation]);

  const onChangeFilterValue = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  const handleChangePage = (page, direction) => {
    if (loadingPagedDetails) return;
    const nextAfterId = data?.pagedApplicationsConfig?.pagination?.nextPage?.afterId;
    const prevAfterId = data?.pagedApplicationsConfig?.pagination?.prevPage?.afterId;
    const afterId = direction ? nextAfterId : prevAfterId;
    setPager(currentPager => ({ ...currentPager, afterId, page }));
  };

  const handleChangeRowsPerPage = pageSize =>
    setPager({
      ...pager,
      pageSize: parseInt(pageSize, defaultPager.pageSize),
      page: defaultPager.page,
      afterId: defaultPager.afterId
    });

  const handleRefresh = () => refetch();

  return (
    localState && (
      <PropertyDetailListComponent
        filters={filters}
        onChangeFilterValue={onChangeFilterValue}
        propertyDetails={localState}
        propertyTypeId={propertyTypeId}
        onPropertyChanged={handlePropertyChanged}
        onAdd={handleAdd}
        onSave={handleSave}
        onDelete={handleDelete}
        onCancel={handleCancel}
        onEdit={handleEdit}
        validation={validation}
        saveDisabled={saveDisabled}
        pager={pager}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onRefresh={handleRefresh}
        loading={loadingPagedDetails}
        isNewProperty={isNewProperty}
      />
    )
  );
};

PropertyDetailListContainer.propTypes = {
  propertyId: PropTypes.number.isRequired,
  propertyTypeId: PropTypes.number
};

export default PropertyDetailListContainer;
